using "./dom";
using "./velement";
using "./vdom";

// A minimal component base: subclasses override Render() to build a
// VElement tree describing the current state, and call the inherited
// Update() whenever that state changes to re-render. Update() DIFFS the
// new VElement tree against the previous one (see vdom.ks's Patch) and
// patches only what changed, reusing real DOM nodes wherever their tag
// stays the same — replacing a whole subtree is now the exception (a
// changed tag, or no previous tree at all), not the default on every
// re-render the way it used to be.
//
// Nested composition: a live child Component can be embedded directly in a
// parent's own VElement tree via `VElement.Mount(child)` (velement.ks) —
// the vdom engine (vdom.ks's Materialize/Patch/PatchChildren) creates,
// patches in place, reorders (given a stable .Id), and tears down a
// mounted child declaratively, the same as any other content mode. This
// is what MountAsChild/PatchAsChild/Teardown below exist for; app code
// calls Mount(parent)/Update() as before and never touches those three
// directly.
class Component : Flushable, Mountable {
  protected VElement Tree;
  private Element ParentElement;
  // Set true only once Mount()/MountAsChild() actually runs, and back to
  // false once Teardown() does (a mounted-child slot this component
  // occupied was replaced or removed — see velement.ks/vdom.ks). A page
  // Component is commonly constructed eagerly (e.g. Router.AddRoute takes
  // an already-built instance — see Router's own header comment) long
  // before it's ever Mount()ed, and two sibling pages sharing one injected
  // service's state<T> (Pure DI — both Subscribe() the same field) both
  // get notified on any change regardless of which one is actually the
  // currently-routed, mounted page. Update() guards on this so that
  // notification is a safe no-op for the unmounted one, instead of a
  // crash (see Update() below) — and the same guard now also covers a
  // component notified again sometime after being torn down.
  private bool IsMounted;

  constructor() {
    this.IsMounted = false;
  }

  public virtual VElement Render() {
    return VElement.Create("div");
  }

  // Overridden to render a fallback UI when Render() throws — a page bug,
  // an unhandled rejected Http call, anything — instead of leaving
  // Mount()/Update() to propagate the exception uncaught, which would
  // otherwise crash whatever triggered the render (a click handler, a
  // Router navigation) with nothing shown to the user at all. Default just
  // re-throws, so anything that doesn't override this keeps today's exact
  // behavior — this is purely additive, opt-in error recovery, not a
  // behavior change for existing components.
  protected virtual VElement RenderError(string message) {
    throw message;
  }

  private VElement SafeRender() {
    try {
      return this.Render();
    } catch (string message) {
      return this.RenderError(message);
    }
  }

  // Called after Mount()/Update() has materialized/patched this
  // component's own VElement tree into `root` — a hook for a component
  // that needs to do additional, imperative work against its OWN
  // now-real root element once it exists. Default is a no-op, so every
  // component that doesn't need this is unaffected. Router still uses
  // this today for its outlet (a single, always-present slot it manages
  // by hand) — a dynamic parent/child relationship (a list of children,
  // conditional presence) is better served by embedding the child via
  // `VElement.Mount(child)` instead (see the class comment above), which
  // gets real reconciliation for free instead of needing this hook at all.
  protected virtual void AfterRender(Element root) {
  }

  public void Mount(Element parent) {
    this.ParentElement = parent;
    this.Tree = this.SafeRender();
    Element root = Materialize(this.Tree, parent);
    parent.appendChild(root);
    this.IsMounted = true;
    this.AfterRender(root);
  }

  // A no-op, not an error, when called before Mount() — see the class-level
  // comment on IsMounted for exactly when this happens for real (a
  // shared-service state change reaching a sibling page that isn't the one
  // currently routed/mounted). Nothing is lost: SafeRender() would only be
  // thrown away unread since there's no live DOM parent to put it in, and
  // Mount() itself always calls SafeRender() fresh whenever this component
  // does become the routed page, picking up whatever the current state is
  // at that point.
  //
  // Batching: while a real DOM event handler Kopular itself attached is
  // still running (Batching.IsActive() — see vdom.ks; every OnClick/
  // OnInput/OnBlur/OnChange listener Materialize/Patch attaches is wrapped
  // in Batching.Run), Update() doesn't render immediately — it registers
  // this component with Batching.Defer and returns. A handler that touches
  // more than one piece of state (or a state<T> write followed by a plain
  // field write Render() also reads) then only ever renders ONCE, once the
  // handler finishes, reading every field's FINAL value for that handler —
  // not once per state<T> write, reading whatever was true at that specific
  // moment. A handler's own statement order no longer matters for what a
  // re-render sees. Fully synchronous — no microtask: by the time the real
  // DOM's own dispatchEvent call returns, every affected component has
  // already re-rendered, the same guarantee React's own synthetic-event
  // batching gives a test's very next assertion. A state<T> write from
  // OUTSIDE a wrapped handler (a setTimeout/setInterval callback, an
  // awaited Http/task continuation, a direct top-level call) is never
  // inside a batch, so Update() still renders immediately there, exactly
  // as before batching existed.
  protected void Update() {
    if (!this.IsMounted) {
      return;
    }
    if (Batching.IsActive()) {
      Batching.Defer(this);
      return;
    }
    this.FlushUpdate();
  }

  // Shared by FlushUpdate and PatchAsChild below — re-renders and patches
  // this component's own tree in place against its CURRENT this.ParentElement
  // (Mount()/MountAsChild() already recorded whichever real element that
  // is, top-level or nested), returning the resulting real root node.
  private Element RenderAndPatch() {
    VElement newTree = this.SafeRender();
    Element root = Patch(this.ParentElement, this.Tree, newTree);
    this.Tree = newTree;
    this.AfterRender(root);
    return root;
  }

  // The real re-render Update() performs immediately outside a batch, or
  // that Batching.Run flushes once for every component deferred during one.
  // Public only because implementing the Flushable interface (see vdom.ks)
  // requires it — KopScript has no "internal" visibility narrower than
  // public, and structural interface conformance requires a public match.
  // Not the intended way to trigger a render from outside this class:
  // calling it directly bypasses batching entirely. Update() is still the
  // real, documented entry point for that.
  public void FlushUpdate() {
    this.RenderAndPatch();
  }

  // Mountable's own entry points (velement.ks) — called by the vdom engine
  // (vdom.ks's Materialize/Patch) when this Component is embedded as a
  // live child in an ANCESTOR's own VElement tree via VElement.Mount(this),
  // not meant to be called directly by app code (public only for interface
  // conformance, same reason FlushUpdate is). Mirrors Mount(parent) minus
  // the internal appendChild — the caller (Materialize/Patch) inserts the
  // returned node itself, the same as every other VElement content mode.
  public Element MountAsChild(Element parent) {
    this.ParentElement = parent;
    this.Tree = this.SafeRender();
    Element root = Materialize(this.Tree, parent);
    this.IsMounted = true;
    this.AfterRender(root);
    return root;
  }

  public Element PatchAsChild() {
    return this.RenderAndPatch();
  }

  // Mountable's teardown entry point — NOT itself meant to be overridden
  // (see OnUnmount below, the real app-facing extension point). Always
  // cascades into this component's own last-rendered tree first, via
  // UnmountTree (vdom.ks), so removing ONE component transitively tears
  // down everything IT mounted too — however many Mounted levels deep —
  // before calling the one real, overridable hook. Public only for the
  // same structural-conformance reason as MountAsChild/PatchAsChild above.
  public void Teardown() {
    UnmountTree(this.Tree);
    this.IsMounted = false;
    this.OnUnmount();
  }

  // Override this — not Teardown — to release anything THIS component
  // holds onto when it's removed from its parent's tree: a state<T>
  // Subscribe made in its own constructor (see state<T>'s own returned
  // unsubscribe handle), a timer, anything else that would otherwise keep
  // running for this component's remaining, now-detached lifetime. Called
  // once, automatically, from Teardown() above — Teardown's own cascade
  // into nested Mounted children still happens regardless of whether (or
  // how) a subclass overrides this. Default is a no-op: purely additive,
  // no existing subclass is affected unless it opts in.
  protected virtual void OnUnmount() {
  }
}
